import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import {
  JOURS_COURTS, iso, nbJoursDuMois, jourSemaine, moisDeSaison, nomMois,
  collecteOuverte, jourFerme, formatLong, fetchAll, aujourdhui, parseIso,
} from '../../utils/planning'
import './MesDisponibilites.css'

function sansReponse(dispos, date, valeur) {
  const n = { ...dispos }
  if (valeur === undefined) delete n[date]
  else n[date] = valeur
  return n
}

export default function MesDisponibilites() {
  const { profile } = useAuth()
  const profileId = profile?.id
  const [saison, setSaison] = useState(null)
  const [jours, setJours] = useState({})
  const [dispos, setDispos] = useState({})
  const [commentaires, setCommentaires] = useState({})
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [ouverts, setOuverts] = useState({})

  const load = useCallback(async () => {
    if (!profileId) return
    setLoading(true)
    const { data: saisons, error } = await supabase
      .from('planning_saisons').select('*').order('date_debut', { ascending: false })
    if (error) {
      setMsg({ type: 'error', text: 'Impossible de charger la saison de planning.' })
      setLoading(false)
      return
    }
    const s = (saisons || []).find(x => x.statut === 'collecte') || (saisons || [])[0] || null
    setSaison(s)
    if (!s) { setLoading(false); return }

    const [rJours, rDispos, rComm] = await Promise.all([
      fetchAll(() => supabase.from('jours_planning')
        .select('date, accueil_ouvert, supervision_ouverte, ferie, libelle')
        .gte('date', s.date_debut).lte('date', s.date_fin).order('date')),
      fetchAll(() => supabase.from('dispos_annuelles')
        .select('date, dispo')
        .eq('superviseur_id', profileId)
        .gte('date', s.date_debut).lte('date', s.date_fin).order('date')),
      supabase.from('commentaires_dispos')
        .select('mois, texte').eq('superviseur_id', profileId).eq('saison_id', s.id),
    ])
    if (rJours.error || rDispos.error || rComm.error) {
      setMsg({ type: 'error', text: 'Erreur lors du chargement de tes disponibilités.' })
    }
    const mj = {}
    for (const j of rJours.data) mj[j.date] = j
    const md = {}
    for (const d of rDispos.data) md[d.date] = d.dispo
    const mc = {}
    for (const c of rComm.data || []) mc[c.mois] = c.texte
    setJours(mj)
    setDispos(md)
    setCommentaires(mc)
    setLoading(false)
  }, [profileId])

  useEffect(() => { load() }, [load])

  const editable = collecteOuverte(saison)

  async function enregistrer(dates, valeur) {
    if (valeur === undefined) {
      const { error } = await supabase.from('dispos_annuelles')
        .delete().eq('superviseur_id', profileId).in('date', dates)
      return error
    }
    const maintenant = new Date().toISOString()
    const rows = dates.map(date => ({ superviseur_id: profileId, date, dispo: valeur, updated_at: maintenant }))
    const { error } = await supabase.from('dispos_annuelles')
      .upsert(rows, { onConflict: 'superviseur_id,date' })
    return error
  }

  async function basculer(date) {
    if (!editable) return
    const avant = dispos[date]
    const apres = avant === undefined ? true : avant === true ? false : undefined
    setDispos(d => sansReponse(d, date, apres))
    setMsg(null)
    const error = await enregistrer([date], apres)
    if (error) {
      setDispos(d => sansReponse(d, date, avant))
      setMsg({ type: 'error', text: "Ta réponse n'a pas pu être enregistrée. La collecte est peut-être terminée." })
    }
  }

  async function appliquerMois(annee, mois, valeur) {
    if (!editable) return
    const dates = []
    for (let j = 1; j <= nbJoursDuMois(annee, mois); j++) {
      const d = iso(annee, mois, j)
      if (jours[d] && !jourFerme(jours[d])) dates.push(d)
    }
    if (dates.length === 0) return
    const avant = dispos
    setDispos(d => {
      const n = { ...d }
      for (const x of dates) {
        if (valeur === undefined) delete n[x]
        else n[x] = valeur
      }
      return n
    })
    setMsg(null)
    const error = await enregistrer(dates, valeur)
    if (error) {
      setDispos(avant)
      setMsg({ type: 'error', text: "Les réponses du mois n'ont pas pu être enregistrées." })
    }
  }

  async function sauverCommentaire(cle, texte) {
    const t = texte.trim()
    if (t === (commentaires[cle] || '')) return
    let error
    if (t) {
      const r = await supabase.from('commentaires_dispos').upsert(
        { superviseur_id: profileId, saison_id: saison.id, mois: cle, texte: t, updated_at: new Date().toISOString() },
        { onConflict: 'superviseur_id,saison_id,mois' })
      error = r.error
    } else {
      const r = await supabase.from('commentaires_dispos').delete()
        .eq('superviseur_id', profileId).eq('saison_id', saison.id).eq('mois', cle)
      error = r.error
    }
    if (error) {
      setMsg({ type: 'error', text: "Le commentaire n'a pas pu être enregistré." })
      return
    }
    setCommentaires(c => {
      const n = { ...c }
      if (t) n[cle] = t
      else delete n[cle]
      return n
    })
  }

  const compteurs = useMemo(() => {
    const ouverts = Object.keys(jours).filter(d => !jourFerme(jours[d]))
    let oui = 0
    let non = 0
    for (const d of ouverts) {
      if (dispos[d] === true) oui += 1
      else if (dispos[d] === false) non += 1
    }
    return { oui, non, restant: ouverts.length - oui - non }
  }, [jours, dispos])

  if (loading) return <div className="spinner" />

  if (!saison) {
    return (
      <div className="md-page">
        <div className="page-header"><div><h2>Mes disponibilités</h2></div></div>
        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
        <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
          Aucune saison de planning n'est configurée pour le moment.
        </div>
      </div>
    )
  }

  let bandeau
  if (editable) {
    bandeau = (
      <div className="alert alert-info">
        La collecte est ouverte{saison.dispos_cloture ? ` jusqu'au ${formatLong(saison.dispos_cloture)}` : ''}.
        Chaque clic est enregistré automatiquement.
      </div>
    )
  } else if (saison.statut === 'collecte') {
    bandeau = (
      <div className="alert alert-warning">
        La saisie n'est pas ouverte en ce moment
        {saison.dispos_ouverture ? ` (ouverture le ${formatLong(saison.dispos_ouverture)})` : ''}.
      </div>
    )
  } else {
    bandeau = (
      <div className="alert alert-warning">
        La collecte est terminée. Pour modifier une disponibilité, contacte la coordination.
      </div>
    )
  }

  return (
    <div className="md-page">
      <div className="page-header">
        <div>
          <h2>Mes disponibilités</h2>
          <div className="page-header-sub">Saison {saison.libelle}</div>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      {bandeau}

      <div className="md-stats">
        <div className="md-stat">
          <div className="md-stat-label">Disponible</div>
          <div className="md-stat-value text-success">{compteurs.oui}</div>
        </div>
        <div className="md-stat">
          <div className="md-stat-label">Indisponible</div>
          <div className="md-stat-value text-error">{compteurs.non}</div>
        </div>
        <div className="md-stat">
          <div className="md-stat-label">À renseigner</div>
          <div className="md-stat-value">{compteurs.restant}</div>
        </div>
      </div>

      <div className="md-legend">
        <span>Clique sur un jour : disponible, puis indisponible, puis non renseigné.</span>
        <span className="md-legend-item"><span className="md-legend-swatch" style={{ background: '#E0F0E4', borderColor: '#9CCBA6' }} />Disponible</span>
        <span className="md-legend-item"><span className="md-legend-swatch" style={{ background: '#F5E0E0', borderColor: '#E0B0B0' }} />Indisponible</span>
        <span className="md-legend-item"><span className="md-legend-swatch" />Non renseigné</span>
        <span className="md-legend-item"><span className="md-legend-swatch" style={{ background: '#E8E2D4' }} />Fermé</span>
      </div>

      <div className="md-mois-grid">
        {moisDeSaison(saison).map(({ annee, mois }, indexMois) => {
          const cle = iso(annee, mois, 1)
          // Accordeon : le mois en cours est ouvert par defaut (le premier mois si hors saison)
          const t = parseIso(aujourdhui())
          const listeMois = moisDeSaison(saison)
          const courant = listeMois.some(m => m.annee === t.annee && m.mois === t.mois)
          const ouvertParDefaut = courant ? (annee === t.annee && mois === t.mois) : indexMois === 0
          const ouvert = cle in ouverts ? ouverts[cle] : ouvertParDefaut
          let nbOui = 0
          let nbNon = 0
          let nbRestant = 0
          const decalage = jourSemaine(cle)
          const cases = []
          for (let i = 0; i < decalage; i++) cases.push(<span key={`v${i}`} className="md-jour md-vide" />)
          for (let j = 1; j <= nbJoursDuMois(annee, mois); j++) {
            const date = iso(annee, mois, j)
            const info = jours[date]
            const ferme = jourFerme(info)
            const val = dispos[date]
            if (!ferme) {
              if (val === true) nbOui += 1
              else if (val === false) nbNon += 1
              else nbRestant += 1
            }
            const etat = val === true ? 'disponible' : val === false ? 'indisponible' : 'non renseigné'
            let classe = 'md-jour'
            if (ferme) classe += ' md-ferme'
            else if (val === true) classe += ' md-oui'
            else if (val === false) classe += ' md-non'
            let tag = null
            if (info && info.ferie) tag = 'Férié'
            else if (info && !ferme && !info.accueil_ouvert) tag = 'Sans accueil'
            else if (info && !ferme && !info.supervision_ouverte) tag = 'Sans sup.'
            cases.push(
              <button
                key={date}
                type="button"
                className={classe}
                disabled={ferme || !editable}
                onClick={() => basculer(date)}
                title={ferme ? `Fermé${info && info.libelle ? ` (${info.libelle})` : ''}` : formatLong(date)}
                aria-label={`${formatLong(date)} : ${ferme ? 'fermé' : etat}`}
              >
                <span>{j}</span>
                {tag && <span className="md-tag">{tag}</span>}
              </button>
            )
          }
          return (
            <div className="md-mois" key={cle}>
              <button
                type="button"
                className="md-mois-entete"
                aria-expanded={ouvert}
                onClick={() => setOuverts(prev => ({ ...prev, [cle]: !ouvert }))}
              >
                <span className="md-mois-titre">{nomMois(mois)} {annee}</span>
                <span className="md-mois-resume">
                  {nbRestant === 0 ? 'Complet' : `${nbRestant} à renseigner`}
                </span>
                <span className={`md-chevron ${ouvert ? 'md-chevron-ouvert' : ''}`} aria-hidden="true">▾</span>
              </button>
              {ouvert && (
                <div className="md-mois-corps">
                  <div className="md-mois-stats">{nbOui} dispo · {nbNon} indispo</div>
                  {editable && (
                    <div className="md-mois-actions">
                      <button type="button" className="btn-ghost btn-sm" onClick={() => appliquerMois(annee, mois, true)}>Tout oui</button>
                      <button type="button" className="btn-ghost btn-sm" onClick={() => appliquerMois(annee, mois, false)}>Tout non</button>
                      <button type="button" className="btn-ghost btn-sm" onClick={() => appliquerMois(annee, mois, undefined)}>Effacer</button>
                    </div>
                  )}
                  <div className="md-semaine">
                    {JOURS_COURTS.map(j => <span key={j}>{j}</span>)}
                  </div>
                  <div className="md-jours">{cases}</div>
                  <div className="md-note">
                    <label htmlFor={`com-${cle}`}>Commentaire du mois</label>
                    <textarea
                      id={`com-${cle}`}
                      key={cle}
                      defaultValue={commentaires[cle] || ''}
                      disabled={!editable}
                      placeholder="Une précision pour la coordination (facultatif)"
                      onBlur={e => sauverCommentaire(cle, e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
